import React,
{
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef
}
from 'react';

// Hoisted easing map so useImperativeHandle dependency array can stay empty without warning
const EASINGS = {
    linear: (t) => t,
    easeInQuad: (t) => t * t,
    easeOutQuad: (t) => t * (2 - t),
    easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
};

// there is speed animation control for a few reasons.
// exposes: ref.current.animateSpeed(targetSpeed, durationMs, { easing, onDone })
// also allows setting speedFactor prop as initial/base speed, well be using that for a few things :)))
// motion:
// - 'fall' (default): dots drift down and wrap to top
// - 'centerOut': dots spawn near the center, fade in, and drift outward ("passing through space")
const SpaceDots = forwardRef(function SpaceDots(
        {
            count = 120,
            color = '#dcdcdc',
            minSize = 0.8,
            maxSize = 2.6,
            speedFactor = 0.4,
            motion = 'fall',
            centerSpawnRadiusFactor = 0.28,
            centerFadeBaseMs = 300,
            debugShowSpawnRadius = false,
        },
        ref
    ) // EDIT THIS FOR SEQUENCE CHANGES
    {
        const canvasRef = useRef(null);
        const particlesRef = useRef([]);
        const rafRef = useRef(null);
        const currentSpeedRef = useRef(speedFactor);
        const baseSpeedRef = useRef(speedFactor);
        const animRef = useRef(null); // { from, to, startTime, duration, easing, onDone }
        const motionRef = useRef(motion);
        const lastNowRef = useRef(0);
        const maxSizeRef = useRef(maxSize);
        const setMotionInternalRef = useRef(null);
        const centerSpawnRadiusFactorRef = useRef(centerSpawnRadiusFactor);
        const centerFadeBaseMsRef = useRef(centerFadeBaseMs);
        const debugShowSpawnRadiusRef = useRef(debugShowSpawnRadius);

        useEffect(() =>
        {
            maxSizeRef.current = maxSize;
        }, [maxSize]);
        useEffect(() =>
        {
            centerSpawnRadiusFactorRef.current = centerSpawnRadiusFactor;
        }, [centerSpawnRadiusFactor]);
        useEffect(() =>
        {
            centerFadeBaseMsRef.current = centerFadeBaseMs;
        }, [centerFadeBaseMs]);
        useEffect(() =>
        {
            debugShowSpawnRadiusRef.current = debugShowSpawnRadius;
        }, [debugShowSpawnRadius]);

        const setMotionInternal = (nextMotion) =>
        {
            motionRef.current = (nextMotion === 'centerOut') ? 'centerOut' : 'fall';
            // Re-vectorize existing particles so we "use the dots already on screen".
            // (Do not recreate the array; just update per-particle velocity fields.)
            try
            {
                const width = window.innerWidth;
                const height = window.innerHeight;
                const cx = width / 2;
                const cy = height / 2;
                const parts = particlesRef.current;
                const msz = maxSizeRef.current;

                if (motionRef.current === 'centerOut')
                {
                    for (let i = 0; i <parts.length; i++)
                    {
                        const p = parts[i];
                        const dx = p.x - cx;
                        const dy = p.y - cy;
                        const len = Math.hypot(dx, dy) || 1;
                        const nx = dx / len;
                        const ny = dy / len;
                        // keep speeds fairly uniform (small jitter) so it feels like we're passing by them
                        const sizeScale = 0.92 + (p.r / msz) * 0.08;
                        const base = (0.56 + (Math.random() * 2 - 1) * 0.06) * sizeScale;
                        p.vxBase = nx * base;
                        p.vyBaseRad = ny * base;
                        p.vBaseMag = base;
                        // keep existing dots visible; new spawns will fade in
                        p.a = 1;
                    }
                }
                else
                {
                    for (let i = 0; i <parts.length; i++)
                    {
                        const p = parts[i];
                        p.a = 1;
                    }
                }
            }
            catch (e)
            {
                /* ignore */ }
        };

        setMotionInternalRef.current = setMotionInternal;

        useImperativeHandle(ref, () => (
        {
            getSpeed: () => currentSpeedRef.current,
            getMotion: () => motionRef.current,
            setBaseSpeed: (v) =>
            {
                baseSpeedRef.current = v;
                currentSpeedRef.current = v;
            },
            setMotion: (nextMotion) =>
            {
                try
                {
                    if (setMotionInternalRef.current) setMotionInternalRef.current(nextMotion);
                }
                catch (e)
                {}
            },
            animateSpeed: (target, durationMs = 1000, opts = {}) =>
            {
                const
                {
                    easing = 'easeInOutQuad', onDone
                } = opts;
                animRef.current = {
                    from: currentSpeedRef.current,
                    to: target,
                    startTime: performance.now(),
                    duration: Math.max(16, durationMs),
                    easing: EASINGS[easing] || EASINGS.easeInOutQuad,
                    onDone,
                };
            },
            // convenience to animate up then back down to base
            pulseSpeed: async (peak, upMs = 1300, downMs = 500) =>
            {
                return new Promise((resolve) =>
                {
                    const startBase = baseSpeedRef.current;
                    const runDown = () =>
                    {
                        // animate back to base stupidly
                        const doneDown = () => resolve();
                        animRef.current = {
                            from: currentSpeedRef.current,
                            to: startBase,
                            startTime: performance.now(),
                            duration: Math.max(16, downMs),
                            easing: EASINGS.easeOutQuad,
                            onDone: doneDown,
                        };
                    };
                    animRef.current = {
                        from: currentSpeedRef.current,
                        to: peak,
                        startTime: performance.now(),
                        duration: Math.max(16, upMs),
                        easing: EASINGS.easeInQuad,
                        onDone: runDown,
                    };
                });
            },
        }), []);

        useEffect(() =>
        {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');

            let width = window.innerWidth;
            let height = window.innerHeight;
            const dpr = Math.max(1, window.devicePixelRatio || 1);

            function resize()
            {
                width = window.innerWidth;
                height = window.innerHeight;
                canvas.style.width = width + 'px';
                canvas.style.height = height + 'px';
                canvas.width = Math.floor(width * dpr);
                canvas.height = Math.floor(height * dpr);
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            }

            function createParticles()
            {
                const particles = [];
                const cx = width / 2;
                const cy = height / 2;
                const spawnRadius = Math.min(width, height) * Math.max(0.02, centerSpawnRadiusFactorRef.current); // spawn anywhere within a center radius
                for (let i = 0; i <count; i++)
                {
                    const r = Math.random() * (maxSize - minSize) + minSize;

                    const p = {
                        x: Math.random() * width,
                        y: Math.random() * height,
                        r,
                        vyBase: (Math.random() * 0.6 + 0.2) * (r / maxSize + 0.5), // base vertical speed unit
                        vxBase: 0,
                        vyBaseRad: 0,
                        vBaseMag: 0,
                        a: 1,
                    };

                    if (motionRef.current === 'centerOut')
                    {
                        // spawn close to center with a random radial direction
                        const ang = Math.random() * Math.PI * 2;
                        const mag = Math.sqrt(Math.random()) * spawnRadius;
                        p.x = cx + Math.cos(ang) * mag;
                        p.y = cy + Math.sin(ang) * mag;
                        // keep speed fairly consistent across particles (tiny jitter)
                        const sizeScale = 0.92 + (r / maxSize) * 0.08;
                        const base = (0.56 + (Math.random() * 2 - 1) * 0.06) * sizeScale;
                        p.vxBase = Math.cos(ang) * base;
                        p.vyBaseRad = Math.sin(ang) * base;
                        p.vBaseMag = base;
                        p.a = 0; // fade-in for center spawns
                    }

                    particles.push(p);
                }
                particlesRef.current = particles;
            }

            function applySpeedAnimation(now)
            {
                const anim = animRef.current;
                if (!anim) return;
                const
                {
                    from,
                    to,
                    startTime,
                    duration,
                    easing,
                    onDone
                } = anim;
                const t = Math.min(1, (now - startTime) / duration);
                const eased = easing(t);
                currentSpeedRef.current = from + (to - from) * eased;
                if (t >= 1)
                {
                    animRef.current = null;
                    if (onDone)
                    {
                        try
                        {
                            onDone();
                        }
                        catch (e)
                        {
                            /* ignore */ }
                    }
                }
            }

            function respawnCenterOut(p, cx, cy)
            {
                const spawnRadius = Math.min(width, height) * Math.max(0.02, centerSpawnRadiusFactorRef.current);
                const ang = Math.random() * Math.PI * 2;
                const mag = Math.sqrt(Math.random()) * spawnRadius;
                p.x = cx + Math.cos(ang) * mag;
                p.y = cy + Math.sin(ang) * mag;
                const sizeScale = 0.92 + (p.r / maxSize) * 0.08;
                const base = (0.56 + (Math.random() * 2 - 1) * 0.06) * sizeScale;
                p.vxBase = Math.cos(ang) * base;
                p.vyBaseRad = Math.sin(ang) * base;
                p.vBaseMag = base;
                p.a = 0;
            }

            function draw(now)
            {
                applySpeedAnimation(now);

                const last = lastNowRef.current || now;
                lastNowRef.current = now;
                const dt = Math.min(40, Math.max(0, now - last));
                const dtScale = dt / 16.6667;

                ctx.clearRect(0, 0, width, height);
                ctx.fillStyle = color;
                const particles = particlesRef.current;
                const speed = currentSpeedRef.current;
                const mode = motionRef.current;
                const cx = width / 2;
                const cy = height / 2;
                const baseFadeInMs = Math.max(60, Number(centerFadeBaseMsRef.current) || 300);
                const margin = 18;
                const spawnRadius = Math.min(width, height) * Math.max(0.02, centerSpawnRadiusFactorRef.current);

                // Batch as many dots as possible into a single fill() call.
                // Only fade-in dots need per-dot alpha draws.
                const batchPath = new Path2D();
                const invCx = 1 / (cx || 1);
                const invCy = 1 / (cy || 1);

                for (let i = 0; i <particles.length; i++)
                {
                    const p = particles[i];

                    if (mode === 'centerOut')
                    {
                        const dx = p.x - cx;
                        const dy = p.y - cy;
                        // edge-based scaling (sides faster than center)
                        const edge = Math.max(Math.abs(dx) * invCx, Math.abs(dy) * invCy); // 0..1
                        const edgeBoost = Math.pow(Math.max(0, Math.min(1, edge)), 1.6);
                        const speedScale = Math.min(3, 0.9 + speed * 0.03);
                        // baseline < 1 so dots near center start slower
                        const baseAccel = 0.38;
                        const accel = baseAccel + edgeBoost * 4 * speedScale;

                        if (p.a < 1)
                        {
                            const v = (p.vBaseMag || 0.56) * Math.max(0.01, speed) * accel;
                            const fadeInMs = Math.max(80, Math.min(baseFadeInMs, baseFadeInMs / (0.65 + v * 1.1)));
                            p.a = Math.min(1, p.a + (dt / fadeInMs));
                        }

                        p.x += p.vxBase * speed * dtScale * accel;
                        p.y += p.vyBaseRad * speed * dtScale * accel;

                        if (
                            (p.x + p.r < -margin) ||
                            (p.x - p.r > width + margin) ||
                            (p.y + p.r < -margin) ||
                            (p.y - p.r > height + margin)
                        )
                        {
                            respawnCenterOut(p, cx, cy);
                        }

                        if (p.a >= 0.999)
                        {
                            batchPath.moveTo(p.x + p.r, p.y);
                            batchPath.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                        }
                        else
                        {
                            ctx.globalAlpha = p.a;
                            ctx.beginPath();
                            ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                            ctx.fill();
                            ctx.globalAlpha = 1;
                        }
                    }
                    else
                    {
                        // legacy: drifting down
                        p.a = 1;
                        p.y += p.vyBase * speed * dtScale;
                        if (p.y - p.r > height)
                        {
                            p.y = -p.r;
                            p.x = Math.random() * width;
                        }

                        batchPath.moveTo(p.x + p.r, p.y);
                        batchPath.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                    }
                }

                // fill batched full-alpha dots
                ctx.globalAlpha = 1;
                ctx.fill(batchPath);

                // Debug: show spawn radius when in centerOut mode
                if (debugShowSpawnRadiusRef.current && mode === 'centerOut')
                {
                    try
                    {
                        ctx.save();
                        ctx.globalAlpha = 1;
                        ctx.strokeStyle = '#ff0000';
                        ctx.lineWidth = 2;
                        ctx.beginPath();
                        ctx.arc(cx, cy, spawnRadius, 0, Math.PI * 2);
                        ctx.stroke();
                        ctx.restore();
                    }
                    catch (e)
                    {
                        /* ignore */ }
                }
                rafRef.current = requestAnimationFrame(draw);
            }

            resize();
            createParticles();
            rafRef.current = requestAnimationFrame(draw);
            window.addEventListener('resize', resize);

            return () =>
            {
                window.removeEventListener('resize', resize);
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
            };
        }, [count, color, minSize, maxSize]);

        useEffect(() =>
        {
            // allow switching motion via prop without recreating particles
            try
            {
                if (setMotionInternalRef.current) setMotionInternalRef.current(motion);
            }
            catch (e)
            {
                /* ignore */ }
        }, [motion]);

        useEffect(() =>
        {
            // update base speed if prop changes, i fucking hate mathing this shit dude but it works
            baseSpeedRef.current = speedFactor;
            if (!animRef.current) currentSpeedRef.current = speedFactor; // only snap when not animating
        }, [speedFactor]);

        return ( <canvas ref = {
                canvasRef
            }
            className = "space-dots-canvas"
            aria-hidden = "true"
            style = {
                {
                    position: 'fixed',
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: 'none',
                    display: 'block'
                }
            }
            />
        );
    });

export default SpaceDots;
